import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ApprovalSectionData, GameFormData } from '@/types/unifiedGameForm';
import { GameWithDetails } from '@/types/gameSystem';
import { CheckCircle, Clock, AlertCircle, Loader2, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { toast } from 'sonner';

interface ApprovalSectionProps {
  gameData: GameWithDetails;
  formData: GameFormData;
  canEdit: boolean;
  canApprove: boolean;
  onSubmitApproval: (role: string) => void;
  saving: boolean;
  userRole: string;
}

export const ApprovalSection: React.FC<ApprovalSectionProps> = ({
  gameData,
  formData,
  canEdit,
  canApprove,
  onSubmitApproval,
  saving,
  userRole
}) => {
  const { user } = useAuthenticatedUser();
  const [playerHasAttending, setPlayerHasAttending] = useState(false);
  const [approvingAsPlayer, setApprovingAsPlayer] = useState(false);
  const [totalAttendingPlayers, setTotalAttendingPlayers] = useState(0);
  const [uniquePlayerApprovals, setUniquePlayerApprovals] = useState(0);

  // Check attendance and approval counts
  useEffect(() => {
    const checkAttendanceAndApprovals = async () => {
      if (!gameData.id) return;

      // Count total attending players
      const { data: attendingData, error: attendingError } = await supabase
        .from('player_attendance_status')
        .select('player_id')
        .eq('game_id', gameData.id)
        .eq('attendance_status', 'attending');

      if (!attendingError && attendingData) {
        setTotalAttendingPlayers(attendingData.length);
      }

      // Count unique player approvals
      const { data: approvalData, error: approvalError } = await supabase
        .from('game_approval_hierarchy')
        .select('approver_id')
        .eq('game_id', gameData.id)
        .eq('approver_role', 'player')
        .eq('approval_status', 'approved');

      if (!approvalError && approvalData) {
        const uniqueApprovers = new Set(approvalData.map(a => a.approver_id));
        setUniquePlayerApprovals(uniqueApprovers.size);
      }

      // Check if current user has confirmed attendance (for showing approve button)
      if (user && userRole === 'player') {
        const { data, error } = await supabase
          .from('player_attendance_status')
          .select('attendance_status')
          .eq('game_id', gameData.id)
          .eq('player_id', user.id)
          .maybeSingle();

        if (!error && data) {
          setPlayerHasAttending(data.attendance_status === 'attending');
        }
      }
    };

    checkAttendanceAndApprovals();
  }, [user, gameData.id, userRole]);

  // Auto-sync database when all approvals are complete
  useEffect(() => {
    const syncApprovalStatus = async () => {
      if (!gameData.id) return;
      
      // Check if UI shows all approvals complete
      const allApprovalsComplete = checkPlayerApprovalStatus() && 
                                   formData.coachApproved && 
                                   formData.venueApproved;
      
      // Check if database is out of sync
      const databaseOutOfSync = allApprovalsComplete && 
                               (formData.gameFormStatus !== 'approved' || 
                                gameData.game_status !== 'confirmed');
      
      if (databaseOutOfSync) {
        console.log('🔄 Syncing approval status to database...');
        
        // Update game approval status
        const { error } = await supabase
          .from('games')
          .update({
            player_approved: true,
            coach_approved: true,
            venue_approved: true,
            game_status: 'confirmed',
            game_form_status: 'approved',
            updated_at: new Date().toISOString()
          })
          .eq('id', gameData.id);
        
        if (!error) {
          console.log('✅ Game status synced successfully');
          toast.success('Game confirmed! All approvals complete.');
          
          // Log the auto-sync action
          await supabase.from('game_actions').insert({
            game_id: gameData.id,
            action_type: 'auto_approval_sync',
            performed_by: user?.id,
            performed_by_type: 'supabase',
            role: userRole || 'system',
            reason: 'Auto-synced approval status based on completed approvals'
          });
          
          // Trigger parent component refresh
          onSubmitApproval('system');
        }
      }
    };
    
    syncApprovalStatus();
  }, [uniquePlayerApprovals, totalAttendingPlayers, formData.coachApproved, formData.venueApproved, gameData.id, gameData.game_status, formData.gameFormStatus]);

  const handlePlayerApproval = async () => {
    if (!user || !gameData.id) return;

    setApprovingAsPlayer(true);
    try {
      // Check if this player has already approved
      const { data: existingApproval } = await supabase
        .from('game_approval_hierarchy')
        .select('id')
        .eq('game_id', gameData.id)
        .eq('approver_role', 'player')
        .eq('approver_id', user.id)
        .maybeSingle();

      if (existingApproval) {
        toast.info('You have already approved this game');
        setApprovingAsPlayer(false);
        return;
      }

      // Create approval record for THIS player only
      const { error } = await supabase
        .from('game_approval_hierarchy')
        .insert({
          game_id: gameData.id,
          game_type: 'regular',
          approval_level: 1,
          approver_role: 'player',
          approver_id: user.id,
          approval_status: 'approved',
          approval_data: {}
        });

      if (error) {
        console.error('Error creating player approval:', error);
        toast.error('Failed to approve as player');
        return;
      }

      // Reload approval counts
      const { data: approvalData } = await supabase
        .from('game_approval_hierarchy')
        .select('approver_id')
        .eq('game_id', gameData.id)
        .eq('approver_role', 'player')
        .eq('approval_status', 'approved');

      const uniqueApprovers = new Set(approvalData?.map(a => a.approver_id) || []);
      setUniquePlayerApprovals(uniqueApprovers.size);

      // Only mark game as fully player-approved if ALL attending players have approved
      if (uniqueApprovers.size >= totalAttendingPlayers && totalAttendingPlayers > 0) {
        const { error: updateError } = await supabase
          .from('games')
          .update({ 
            player_approved: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', gameData.id);

        if (updateError) {
          console.error('Error updating game approval:', updateError);
        } else {
          toast.success('All players have approved! Player approval complete.');
          
          // Re-query current approval state from database before auto-confirming
          const { data: currentGameData, error: gameError } = await supabase
            .from('games')
            .select('player_approved, coach_approved, venue_approved')
            .eq('id', gameData.id)
            .single();

          if (!gameError && currentGameData) {
            const allApproved = currentGameData.player_approved && 
                               currentGameData.coach_approved && 
                               currentGameData.venue_approved;
            
            if (allApproved) {
              console.log('✅ All approvals complete! Auto-confirming game...');
              
              // Auto-update game status to confirmed
              const { error: statusError } = await supabase
                .from('games')
                .update({
                  game_status: 'confirmed',
                  game_form_status: 'approved'
                })
                .eq('id', gameData.id);

              if (statusError) {
                console.error('❌ Error auto-confirming game:', statusError);
                toast.error('Failed to auto-confirm game');
              } else {
                console.log('✅ Game auto-confirmed successfully!');
                toast.success('🎉 Game automatically confirmed! All approvals complete.');
              }
            }
          }
          
          onSubmitApproval('player');
        }
      } else {
        toast.success(`Your approval recorded (${uniqueApprovers.size} of ${totalAttendingPlayers} players approved)`);
      }
    } catch (error) {
      console.error('Error in player approval:', error);
      toast.error('Failed to approve as player');
    } finally {
      setApprovingAsPlayer(false);
    }
  };

  const getApprovalIcon = (approved: boolean) => {
    return approved ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <Clock className="h-4 w-4 text-yellow-500" />
    );
  };

  const getApprovalBadge = (approved: boolean) => {
    return (
      <Badge variant={approved ? 'default' : 'secondary'}>
        {approved ? 'Approved' : 'Pending'}
      </Badge>
    );
  };

  const canUserApprove = (role: string) => {
    return canApprove && userRole === role && !getApprovalStatus(role);
  };

  const getApprovalStatus = (role: string) => {
    switch (role) {
      case 'player': return checkPlayerApprovalStatus();
      case 'coach': return formData.coachApproved;
      case 'venue': return formData.venueApproved;
      default: return false;
    }
  };

  // Check actual player approval status - requires ALL attending players to approve
  const checkPlayerApprovalStatus = () => {
    // Only return true if we have approvals from ALL attending players
    return totalAttendingPlayers > 0 && uniquePlayerApprovals >= totalAttendingPlayers;
  };

  const handleApproval = (role: string) => {
    onSubmitApproval(role);
  };

  const [overriding, setOverriding] = useState(false);
  
  const handleOverrideAndConfirm = async () => {
    if (!window.confirm('Are you sure you want to override and manually confirm this game? This will mark all approvals as complete.')) {
      return;
    }

    setOverriding(true);
    try {
      // Update game to mark all approvals complete
      // Try venue_games first, fall back to games
      const { error: updateError } = await supabase
        .from('games')
        .update({
          player_approved: true,
          coach_approved: true,
          venue_approved: true,
          game_status: 'confirmed',
          game_form_status: 'approved'
        })
        .eq('id', gameData.id);

      if (updateError) throw updateError;

      // Log the override action
      await supabase.from('game_actions').insert({
        game_id: gameData.id,
        action_type: 'approval_override',
        performed_by: user?.id,
        performed_by_type: 'supabase',
        role: userRole || 'venue',
        reason: 'Manual override by venue admin to confirm game'
      });

      toast.success('Game confirmed successfully!');
      window.location.reload();
    } catch (error) {
      console.error('Error overriding game confirmation:', error);
      toast.error('Failed to override game confirmation');
    } finally {
      setOverriding(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Approval Workflow
        </CardTitle>
        <CardDescription>
          Three-tier approval system: Player → Coach → Venue
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Approval Hierarchy */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">Approval Status</h4>
          
          {/* Player Approval */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              {getApprovalIcon(checkPlayerApprovalStatus())}
              <div>
                <div className="font-medium">Player Approval</div>
                <div className="text-sm text-muted-foreground">
                  {totalAttendingPlayers > 0 
                    ? `${uniquePlayerApprovals} of ${totalAttendingPlayers} players have approved`
                    : 'Waiting for players to confirm attendance'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getApprovalBadge(checkPlayerApprovalStatus())}
              {userRole === 'player' && playerHasAttending && !checkPlayerApprovalStatus() && (
                <Button 
                  size="sm" 
                  onClick={handlePlayerApproval}
                  disabled={approvingAsPlayer}
                >
                  {approvingAsPlayer ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Approve as Player'
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Coach Approval */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              {getApprovalIcon(formData.coachApproved)}
              <div>
                <div className="font-medium">Coach Approval</div>
                <div className="text-sm text-muted-foreground">
                  Coach confirms team lineup and game details
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getApprovalBadge(formData.coachApproved)}
              {canUserApprove('coach') && (
                <Button 
                  size="sm" 
                  onClick={() => handleApproval('coach')}
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Approve as Coach'
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Venue Approval */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              {getApprovalIcon(formData.venueApproved)}
              <div>
                <div className="font-medium">Venue Final Approval</div>
                <div className="text-sm text-muted-foreground">
                  Venue provides final approval and can override decisions
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getApprovalBadge(formData.venueApproved)}
              {canUserApprove('venue') && (
                <Button 
                  size="sm" 
                  onClick={() => handleApproval('venue')}
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Provide Final Approval'
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Overall Status */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">Overall Status</h4>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              {checkPlayerApprovalStatus() && formData.coachApproved && formData.venueApproved ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              )}
              <div>
                <div className="font-medium">Game Status</div>
                <div className="text-sm text-muted-foreground">
                  {checkPlayerApprovalStatus() && formData.coachApproved && formData.venueApproved
                    ? 'All approvals complete - Game ready'
                    : 'Waiting for required approvals'}
                </div>
              </div>
            </div>
            <Badge 
              variant={checkPlayerApprovalStatus() && formData.coachApproved && formData.venueApproved ? 'default' : 'secondary'}
            >
              {formData.gameFormStatus.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>

          {/* Override Button for Venue/Admin */}
          {userRole === 'venue' && !(checkPlayerApprovalStatus() && formData.coachApproved && formData.venueApproved) && (
            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-amber-900 dark:text-amber-100">Venue Override Available</h5>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    As venue staff, you can manually confirm this game if all participants are actually ready.
                  </p>
                </div>
              </div>
              <Button
                variant="default"
                size="sm"
                onClick={handleOverrideAndConfirm}
                disabled={overriding}
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                {overriding ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Confirming...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Override and Confirm Game
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Approval Notes */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Approval Notes</h4>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>• Player approval confirms attendance and personal statistics</p>
            <p>• Coach approval validates team decisions and game strategy</p>
            <p>• Venue approval is the final authority and can override previous decisions</p>
            <p>• All three approvals are required for game completion</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};